import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ScheduledEvent, YoutubeChannelVideo } from "@tih/shared";
import { SITE_COPY_CONTENT_KEY } from "@tih/shared";
import {
  ApiError,
  adminFetchContent,
  adminFetchEvents,
  adminFetchYoutubeVideos,
  adminPatchContent,
} from "../../api/client";
import { useSiteCopy } from "../../context/SiteCopyContext";
import { eventDisplayTitle, formatEventWhen } from "../../lib/event-admin-utils";
import {
  extractYoutubeId,
  parseHomepageVideo,
  type HomepageVideo,
} from "../../lib/homepage-video";

const EMPTY: HomepageVideo = {
  published: false,
  youtubeId: "",
  title: "",
  subtitle: "",
  eventId: null,
};

export function AdminHomepageVideoSection() {
  const { refresh } = useSiteCopy();
  const [draft, setDraft] = useState<HomepageVideo>(EMPTY);
  const [events, setEvents] = useState<ScheduledEvent[]>([]);
  const [videos, setVideos] = useState<YoutubeChannelVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [content, eventRows, yt] = await Promise.all([
          adminFetchContent(SITE_COPY_CONTENT_KEY).catch((err) => {
            if (err instanceof ApiError && err.status === 404) {
              return { valueJson: {} };
            }
            throw err;
          }),
          adminFetchEvents(),
          adminFetchYoutubeVideos().catch(() => ({ videos: [] as YoutubeChannelVideo[] })),
        ]);
        if (cancelled) return;
        const parsed = parseHomepageVideo(
          (content.valueJson as Record<string, unknown> | undefined)?.homepageVideo,
        );
        setDraft(parsed ?? EMPTY);
        setEvents(eventRows);
        setVideos(yt.videos);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Could not load homepage video");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  function pickVideo(video: YoutubeChannelVideo) {
    setDraft((prev) => ({
      ...prev,
      youtubeId: video.videoId,
      title: prev.title.trim() || video.title,
    }));
  }

  async function save(next: HomepageVideo) {
    const youtubeId = extractYoutubeId(next.youtubeId) ?? next.youtubeId.trim();
    if (next.published && !youtubeId) {
      setError("Add a YouTube video id (or URL) before publishing.");
      return;
    }
    const payload: HomepageVideo = { ...next, youtubeId };
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await adminPatchContent(SITE_COPY_CONTENT_KEY, { homepageVideo: payload });
      setDraft(payload);
      await refresh();
      setSuccess(
        payload.published
          ? "Homepage replay published."
          : "Homepage replay hidden. A live broadcast will still appear.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save homepage video");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="admin-dashboard-section">
      <div className="admin-dashboard-heading-row">
        <h2 className="admin-dashboard-heading">Landing page video</h2>
        <Link to="/admin/youtube" className="btn btn-ghost">
          Video library
        </Link>
      </div>
      <p className="admin-card-sub">
        Controls the replay on the public landing / coming-soon page. When an upcoming event
        has a poster, that poster leads and this replay sits underneath — hide it here when
        you are promoting a new night. A live broadcast still takes over while on air. You can
        also toggle from <strong>Events → Stream → Landing page</strong>.
      </p>

      {loading ? (
        <p className="admin-card-sub">Loading…</p>
      ) : (
        <div className="admin-card">
          {error && <div className="form-error">{error}</div>}
          {success && <div className="form-success">{success}</div>}

          <div
            className={`admin-landing-video-status${draft.published ? " is-on" : " is-off"}`}
            role="status"
          >
            {draft.published ? (
              <>
                <strong>On the landing page now</strong>
                <span>
                  {draft.youtubeId
                    ? `YouTube ${draft.youtubeId}`
                    : "Published (add a video id below)"}
                  {draft.eventId != null ? ` · event #${draft.eventId}` : ""}
                </span>
              </>
            ) : (
              <>
                <strong>Hidden from landing</strong>
                <span>Upcoming event poster leads the home page. Publish only when you want a replay under it.</span>
              </>
            )}
          </div>

          <div className="admin-card-row">
            <label className="admin-field">
              <span>YouTube id or URL</span>
              <input
                value={draft.youtubeId}
                onChange={(e) => setDraft((prev) => ({ ...prev, youtubeId: e.target.value }))}
                placeholder="dQw4w9WgXcQ or https://youtu.be/…"
              />
            </label>
            <label className="admin-field">
              <span>Title</span>
              <input
                value={draft.title}
                onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Together In Heaven · Worship Overnight"
              />
            </label>
            <label className="admin-field">
              <span>Subtitle</span>
              <input
                value={draft.subtitle}
                onChange={(e) => setDraft((prev) => ({ ...prev, subtitle: e.target.value }))}
                placeholder="Full overnight replay"
              />
            </label>
            <label className="admin-field">
              <span>Linked event (optional)</span>
              <select
                value={draft.eventId ?? ""}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    eventId: e.target.value ? Number(e.target.value) : null,
                  }))
                }
              >
                <option value="">None</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {eventDisplayTitle(ev)} · {formatEventWhen(ev.startsAt)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="admin-field admin-field-check">
            <input
              type="checkbox"
              checked={draft.published}
              onChange={(e) => setDraft((prev) => ({ ...prev, published: e.target.checked }))}
            />
            <span>Published on the homepage</span>
          </label>

          <div className="admin-dashboard-links">
            {draft.published ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={saving}
                onClick={() => void save({ ...draft, published: false })}
              >
                {saving ? "Saving…" : "Hide from landing page"}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                disabled={saving}
                onClick={() => void save({ ...draft, published: true })}
              >
                {saving ? "Saving…" : "Show on landing page"}
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost"
              disabled={saving}
              onClick={() => void save(draft)}
            >
              Save details
            </button>
            {draft.published && (
              <button
                type="button"
                className="btn btn-ghost"
                disabled={saving}
                onClick={() => void save({ ...draft, published: true })}
              >
                Publish updates
              </button>
            )}
          </div>

          {videos.length > 0 && (
            <div className="admin-table-wrap" style={{ marginTop: 16 }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Channel upload</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {videos.slice(0, 8).map((v) => (
                    <tr key={v.videoId}>
                      <td>{v.title}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => pickVideo(v)}
                        >
                          Use this video
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
